import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MfaService } from 'src/app/services/mfa.service';

/**
 * MfaEnrollComponent is the reusable presentational piece for enrolling MFA.
 * It is used both on the standalone login setup page and inside the profile
 * "enable MFA" dialog.
 *
 * When `setupToken` is provided (login enrollment, no session) it is passed to
 * the MFA endpoints as a one-off auth header; otherwise the AuthInterceptor
 * attaches the current session JWT (profile enrollment).
 *
 * Emits `done` once the user has confirmed the code and acknowledged the backup
 * codes.
 */
@Component({
  selector: 'app-mfa-enroll',
  templateUrl: './mfa-enroll.component.html',
  styleUrl: './mfa-enroll.component.scss',
})
export class MfaEnrollComponent implements OnInit {
  @Input() setupToken?: string;
  @Output() done = new EventEmitter<void>();

  /** UI phases: scan QR + enter code, then show backup codes. */
  step: 'enroll' | 'backup' = 'enroll';

  otpauthUrl: string = '';
  secret: string = '';
  backupCodes: string[] = [];

  loading: boolean = true;
  submitting: boolean = false;
  error: string = '';
  saved: boolean = false;

  codeForm: UntypedFormGroup = new UntypedFormGroup({
    code: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(/^\d{6}$/),
    ]),
  });

  constructor(private _mfaService: MfaService) {}

  ngOnInit(): void {
    this._mfaService.mfaSetup(this.setupToken).subscribe({
      next: (response) => {
        this.loading = false;
        this.otpauthUrl = response.otpauth_url;
        this.secret = response.secret;
      },
      error: () => {
        this.loading = false;
        this.error = 'mfa.setupError';
      },
    });
  }

  confirm() {
    if (!this.codeForm.valid) {
      return;
    }
    this.submitting = true;
    this.error = '';
    const code = this.codeForm.get('code')?.value;
    this._mfaService.mfaConfirm(code, this.setupToken).subscribe({
      next: (response) => {
        this.submitting = false;
        if (response.success) {
          this.backupCodes = response.backup_codes;
          this.step = 'backup';
        } else {
          this.error = 'mfa.codeError';
        }
      },
      error: () => {
        this.submitting = false;
        this.error = 'mfa.codeError';
      },
    });
  }

  finish() {
    if (!this.saved) {
      return;
    }
    this.done.emit();
  }
}
